import { isAutoGeneratedName as checkIsAutoGeneratedName } from "common";
import dayjs from "dayjs";
import { produce } from "immer";
import { lowerCase, merge, round } from "lodash";
import { FC } from "react";
import {
    Field,
    FieldResponse,
    FieldType,
    FieldWithPickupOptions,
    Form,
    FormResponse,
    Guest,
    HeightUnit,
    WeightUnit,
} from "types";
import { now } from "../../helpers/dateHelper";
import { getFields, initializeFieldResponse } from "../../models/Form";
import { convertHeight, convertWeight } from "../../utils/unitHelpers";
import { CheckboxField } from "../Field/CheckboxField";
import { DateOfBirthField, getISOFormattedDateString } from "../Field/DateOfBirthField";
import { RadioField } from "../Field/RadioField";
import { HeightField } from "./HeightField";
import { PickupLocationField } from "./PickupLocationField";
import { SelectField } from "./SelectField";
import { TextareaField } from "./TextareaField";
import { FieldProps, TextField } from "./TextField";
import { UnitField } from "./UnitField";

const REMOVE_PICKUP_OPTION = {
    pickupLocation: {
        id: "__remove__",
        name: "Remove old pickup",
    },
    sequence: 0,
};

/**
 * `fields` must be the exact list of fields the modal renders, in render order: the returned
 * `formResponse.fields` are addressed positionally by the Formik field names, so any divergence
 * writes answers into the wrong slot.
 */
export const getQuestionnaireInitialValues = (fields: (Field | FieldWithPickupOptions)[], guest: Guest) => {
    const formResponse: FormResponse = {
        user: guest.formResponse?.user,
        fields: fields.map((field) => {
            return { ...initializeFieldResponse(field), value: field.value ?? "" };
        }),
    };

    const values = getGuestQuestionnaireFieldResponses(guest, formResponse);
    if (checkIsAutoGeneratedName(values.name)) {
        /** If guest name is auto-generated, we need to set the value to empty so only placeholder would be visible */
        values.name = "";
    }

    if (values.formResponse) {
        return fixQuestionnaireFormFieldValues(values);
    }

    return values;
};

/** `fields` must be the same list, in the same order, that was passed to getQuestionnaireInitialValues. */
export const validateQuestionnaire = (
    fields: (Field | FieldWithPickupOptions)[],
    disableRequiredFields: boolean,
    hasAutoGeneratedName: boolean,
) => {
    return (guest: Guest) => {
        const errors: any = {};

        // Validate the name field
        if (!disableRequiredFields && !hasAutoGeneratedName && !guest.name.trim()) {
            errors.name = "is required";
        }

        // Validate the questionnaire fields
        for (const [index, field] of fields.entries()) {
            const isRequired = field.required && !disableRequiredFields;
            const error = getQuestionnaireFormError(
                field,
                guest.formResponse?.fields?.[index]?.value ?? "",
                isRequired,
            );

            if (error) {
                errors.formResponse = errors.formResponse ?? { fields: [] };
                errors.formResponse.fields[index] = { value: error };
            }
        }

        return errors;
    };
};

/**
 * Normalizes a field key by replacing forward slashes with underscores,
 * removing all whitespace, trimming leading and trailing spaces, and
 * converting the string to lowercase.
 */
const normalizeFieldKey = (key: string) => {
    if (!key) {
        return "";
    }

    return key.replace(/\//g, "_").replace(/\s+/g, "").trim().toLowerCase();
};

/**
 * This function will merge questionnaire form with guest data.
 * if product questionnaire has fields a,b,c
 * and if guest form response has a,d,e
 * Result will have formResponse with a,b,c,d,e
 */
export const getGuestQuestionnaireFieldResponses = (guest: Guest, formResponse: FormResponse): Guest => {
    if (!guest.formResponse) {
        return { ...guest, formResponse };
    }

    const guestFieldsMap = new Map<string, FieldResponse>();
    for (const field of guest.formResponse.fields) {
        if (field.value) {
            guestFieldsMap.set(normalizeFieldKey(field.label), field);
        }
    }

    const updatedFormResponse = produce(formResponse, (draft) => {
        for (let i = 0; i < draft.fields.length; i++) {
            const field = draft.fields[i];
            const normalizedLabel = normalizeFieldKey(field.label);
            const existing = guestFieldsMap.get(normalizedLabel);

            if (existing) {
                let merged = merge({}, field, existing);

                if (field.type === FieldType.WEIGHT && existing.value && existing.unit !== field.unit) {
                    const number = Number(existing.value);

                    if (!isNaN(number)) {
                        const converted = convertWeight(number, existing.unit as WeightUnit, field.unit as WeightUnit);

                        merged.value = converted.toFixed(2);
                        merged.unit = field.unit;
                    }
                }

                if (field.type === FieldType.HEIGHT && existing.value) {
                    const converted = convertHeight(
                        existing.value,
                        existing.unit as HeightUnit,
                        field.unit as HeightUnit,
                    );

                    merged.value = converted;
                    merged.unit = field.unit;
                }

                draft.fields[i] = merged;
                guestFieldsMap.delete(normalizedLabel);
            }
        }

        // Values in guestFieldsMap are those which are in guest.formResponse but not in questionnaire form

        const sortedUnassignedFieldKeys = [...guestFieldsMap.keys()].sort();

        for (const key of sortedUnassignedFieldKeys) {
            const value = guestFieldsMap.get(key);
            if (value) {
                draft.fields.push(value);
            }
        }
    });

    return { ...guest, formResponse: updatedFormResponse };
};

const unexpectedPattern = /(\d{4})\D(\d{2})\D(\d{2})/gm; // occasionally, the dateOfBirth may be wrong formatted

export const fixQuestionnaireFormFieldValues = (guest: Guest) => {
    return produce(guest, (draft) => {
        if (draft.formResponse?.fields) {
            for (const field of draft.formResponse.fields) {
                if (field.type === FieldType.DATE_OF_BIRTH) {
                    const groups: RegExpExecArray | null = unexpectedPattern.exec(field.value);
                    if (groups && (groups?.length ?? 0) >= 4) {
                        // change input of like YYYY-DD-MM to MM/DD/YYYY
                        field.value = `${groups[2]}/${groups[3]}/${groups[1]}`;
                    }
                }
            }
        }
    });
};

const emailRegex = /^[\w.%+-]+@[a-z\d.-]+\.[a-z]{2,}$/i;

export const getQuestionnaireFormError = (field: Field | FieldWithPickupOptions, value: string, required: boolean) => {
    if (required && !value) {
        return "is required";
    }

    if (value && field.type === FieldType.EMAIL) {
        const isValid = emailRegex.test(value);
        return isValid ? "" : 'must be in "someone@example.com" format';
    }

    if (value && field.type === FieldType.DATE_OF_BIRTH) {
        const formattedDate = getISOFormattedDateString(value);
        // api expects the value to be in YYYY-MM-DD format, so validating with that format.
        const date = dayjs(formattedDate, "YYYY-MM-DD", true);

        if (!date.isValid() || date.isAfter(now())) {
            return "is invalid";
        }

        if (field.verifyAge && field.comparator && field.age) {
            const date = dayjs(formattedDate).add(field.age, "years");
            const now = new Date();
            const isValid = field.comparator === "older" ? date.isBefore(now) : date.isAfter(now);

            if (!isValid) {
                return `is invalid - age must be ${field.comparator} than ${field.age}`;
            }
        }
    }

    if (value && field.unit === HeightUnit.FEET) {
        const normalized = value.replace(",", ".").trim();

        const match = normalized.match(/^(\d+)(?:[.,](\d+))?$/);

        if (match) {
            const inchesPart = match[2];

            if (inchesPart) {
                const inches = parseFloat(inchesPart);

                if (inches >= 12) {
                    return "inches cannot be 12 or more";
                }
            }
        }
    }

    // No need to validate other types since each input field (unit, select, checkbox)
    // doesn't allow invalid values
};

/**
 * Returns Questionnaire fields for the form, in case the guest has few fields that are not present in the form,
 * we need to show them as well in the form, so creating fields for them as well and displaying.
 */
export const getGuestQuestionnaireFields = (
    form: Form,
    guest: Guest,
    isPrimaryGuest = false,
    isOrganizerOnly = false,
): (Field | FieldWithPickupOptions)[] => {
    const formFields: (Field | FieldWithPickupOptions)[] = getFields(form, isPrimaryGuest, isOrganizerOnly);
    if (!guest.formResponse) {
        return formFields;
    }

    const guestFieldsMap = new Map<string, FieldResponse>();
    for (const field of guest.formResponse.fields) {
        if (field.value) {
            guestFieldsMap.set(normalizeFieldKey(field.label), field);
        }
    }

    // Removing the keys, for those fields, which is there in the questionnaire form.
    // For PICKUP fields, inject the guest's previous selection as an option if it is no longer
    // present in the new experience's options (e.g. after a product switch).
    for (let i = 0; i < formFields.length; i += 1) {
        const field = formFields[i];
        const normalizedLabel = normalizeFieldKey(field.label);
        const guestResponse = guestFieldsMap.get(normalizedLabel);
        const isLegacyTextPickup = field.type === FieldType.PICKUP && guestResponse?.type === FieldType.TEXT;

        if (normalizedLabel && guestResponse && field.type === FieldType.PICKUP && guestResponse.pickupLocation?.id) {
            const pickupField = field as FieldWithPickupOptions;
            const baseOptions = pickupField.options ?? [];
            const alreadyExists = baseOptions.some(
                (opt) => opt.pickupLocation?.id === guestResponse.pickupLocation!.id,
            );

            let updatedOptions = baseOptions;
            if (!alreadyExists) {
                const maxSequence = Math.max(0, ...baseOptions.map((o) => o.sequence));
                updatedOptions = [
                    ...updatedOptions,
                    {
                        pickupLocation: guestResponse.pickupLocation!,
                        sequence: maxSequence + 1,
                    },
                ];
            }

            // Add "Remove" option if not already present
            const hasRemoveOption = updatedOptions.some((opt) => opt.pickupLocation?.id === "__remove__");
            if (!hasRemoveOption) {
                updatedOptions = [REMOVE_PICKUP_OPTION, ...updatedOptions];
            }

            if (updatedOptions !== baseOptions) {
                formFields[i] = {
                    ...pickupField,
                    options: updatedOptions,
                };
            }
        }

        if (isLegacyTextPickup) {
            continue;
        }

        guestFieldsMap.delete(normalizedLabel);
    }

    const sortedUnassignedFieldKeys = [...guestFieldsMap.keys()].sort();
    for (const key of sortedUnassignedFieldKeys) {
        const field = guestFieldsMap.get(key);
        if (field) {
            // There is no questionnaire field for these
            const defaultFieldValues: Field = {
                id: lowerCase(key),
                readonly: false,
                required: false,
                organizerOnly: false,
                addToSavedViews: false,
                ...field,
            };
            if (defaultFieldValues.type === FieldType.PICKUP) {
                const options = field.pickupLocation
                    ? [REMOVE_PICKUP_OPTION, { pickupLocation: field.pickupLocation, sequence: 1 }]
                    : [];
                const pickupField = {
                    ...defaultFieldValues,
                    options,
                };
                formFields.push(pickupField);
            } else {
                formFields.push(defaultFieldValues);
            }
        }
    }

    return formFields.map((field) => {
        const isPickup = field.type === FieldType.PICKUP;

        if (!isPickup) {
            return field;
        }

        const guestPickup = guest?.formResponse?.fields.find(
            (guestField) =>
                guestField.type === FieldType.SELECT &&
                normalizeFieldKey(guestField.label) === normalizeFieldKey("Pickup location"),
        );

        return guestPickup
            ? {
                  ...field,
                  options: guestPickup.options ?? (guestPickup.value != null ? [guestPickup.value] : []),
                  value: guestPickup.value,
                  type: FieldType.SELECT,
                  readonly: true,
              }
            : field;
    });
};

/**
 * Removes pickup location fields from the form response when the "Remove" option is selected.
 * This should be called before submitting the form response to the API.
 *
 * @param guest - The guest object containing formResponse
 * @returns A new guest object with cleaned formResponse (pickup locations with __remove__ value filtered out)
 */
export const cleanPickupLocationFromFormResponse = (guest: Guest): Guest => {
    if (!guest.formResponse?.fields) {
        return guest;
    }

    return produce(guest, (draft) => {
        if (draft.formResponse) {
            draft.formResponse.fields = draft.formResponse.fields.filter(
                (field) => !(field.type === FieldType.PICKUP && field.value === "__remove__"),
            );
        }
    });
};

export const questionnaireFieldMap: Partial<Record<FieldType, FC<FieldProps>>> = {
    [FieldType.WEIGHT]: UnitField,
    [FieldType.HEIGHT]: HeightField,
    [FieldType.DATE_OF_BIRTH]: DateOfBirthField,
    [FieldType.SELECT]: SelectField,
    [FieldType.PICKUP]: PickupLocationField,
    [FieldType.TEXTAREA]: TextareaField,
    [FieldType.TEXT]: TextField,
    [FieldType.EMAIL]: TextField,
    [FieldType.CHECKBOX]: CheckboxField,
    [FieldType.MARKETING_CONSENT]: RadioField,
    [FieldType.PHONE]: TextField,
};
